import { useCallback, useEffect, useState } from 'react';
import { BREAKPOINTS } from '@/app/constants';
import { ScreenType } from '../constants';

// 屏幕宽度配置类型，与 Tailwind 配置格式一致
export type ScreenConfig = { min?: number; max: number } | { min: number; max?: number };

const ScreenTypePriority = {
  [ScreenType.SM]: 10,
  [ScreenType.MD]: 9,
  [ScreenType.LG]: 8,
  [ScreenType.DEFAULT]: 3,
  [ScreenType.XL]: 4,
  [ScreenType.FHD]: 5,
  [ScreenType.QHD]: 6,
  [ScreenType.QHD_PLUS]: 7,
};

// 重新设计配置，确保每个屏幕尺寸有明确的范围
const SCREEN_CONFIGS: Record<ScreenType, ScreenConfig> = {
  [ScreenType.SM]: { max: BREAKPOINTS.SM_MAX },
  [ScreenType.MD]: { max: BREAKPOINTS.MD_MAX },
  [ScreenType.LG]: { max: BREAKPOINTS.LG_MAX },
  [ScreenType.DEFAULT]: { min: BREAKPOINTS.DEFAULT_MIN },
  [ScreenType.XL]: { min: BREAKPOINTS.XL_MIN },
  [ScreenType.FHD]: { min: BREAKPOINTS.FHD_MIN },
  [ScreenType.QHD]: { min: BREAKPOINTS.QHD_MIN },
  [ScreenType.QHD_PLUS]: { min: BREAKPOINTS.QHD_PLUS_MIN },
};

const checkScreenSize = (width: number, config: ScreenConfig): boolean => {
  const { max = Number.MAX_SAFE_INTEGER, min = 0 } = config;
  return width <= max && width >= min;
};

type ScreenStatusType = {
  isSmScreen: boolean;
  isMdScreen: boolean;
  isLgScreen: boolean;
  isXlScreen: boolean;
  isFhdScreen: boolean;
  isQhdScreen: boolean;
  isQhdPlusScreen: boolean;
  isDefaultScreen: boolean;
};

const ScreenTypeMap: Record<ScreenType, keyof ScreenStatusType> = {
  [ScreenType.SM]: 'isSmScreen',
  [ScreenType.MD]: 'isMdScreen',
  [ScreenType.LG]: 'isLgScreen',
  [ScreenType.XL]: 'isXlScreen',
  [ScreenType.FHD]: 'isFhdScreen',
  [ScreenType.QHD]: 'isQhdScreen',
  [ScreenType.QHD_PLUS]: 'isQhdPlusScreen',
  [ScreenType.DEFAULT]: 'isDefaultScreen',
};

export const useIsScreen = () => {
  const [screenState, setScreenState] = useState<
    ScreenStatusType & {
      matchList: ScreenType[];
    }
  >(() => ({
    isSmScreen: false,
    isMdScreen: false,
    isLgScreen: false,
    isXlScreen: false,
    isFhdScreen: false,
    isQhdScreen: false,
    isQhdPlusScreen: false,
    isDefaultScreen: true,
    matchList: [],
  }));

  const updateScreenState = useCallback(() => {
    const width = window.innerWidth;

    const list = [
      ScreenType.SM,
      ScreenType.MD,
      ScreenType.LG,
      ScreenType.XL,
      ScreenType.FHD,
      ScreenType.QHD,
      ScreenType.QHD_PLUS,
      ScreenType.DEFAULT,
    ].map((type) => {
      const config = SCREEN_CONFIGS[type];
      const isInRange = checkScreenSize(width, config);
      return {
        type,
        isInRange,
      };
    });

    setScreenState({
      ...list.reduce((acc, item) => {
        acc[ScreenTypeMap[item.type]] = item.isInRange;
        return acc;
      }, {} as ScreenStatusType),
      /** 匹配到的屏幕类型列表, 按照优先级排序, 优先级高的在前 */
      matchList:
        list
          .filter((item) => item.isInRange)
          .sort((a, b) => ScreenTypePriority[b.type] - ScreenTypePriority[a.type])
          .map((item) => item.type) ?? [],
    });
  }, []);

  useEffect(() => {
    updateScreenState();
    window.addEventListener('resize', updateScreenState);
    return () => window.removeEventListener('resize', updateScreenState);
  }, [updateScreenState]);

  return screenState;
};
